import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import SprintMomentumRing from "./SprintMomentumRing";

function CompetitiveScoreCard({ score, partnerName = "Partner" }) {
  const viewerScore = Number(score?.competitive?.viewer_score ?? score?.viewer?.showed_up_days ?? 0);
  const partnerScore = Number(score?.competitive?.partner_score ?? score?.partner?.showed_up_days ?? 0);
  const duration = Number(score?.duration_days ?? 7);
  const dayNumber = Number(score?.day_number ?? 1);
  const leader = score?.competitive?.leader || "tie";
  const summary =
    leader === "viewer"
      ? `You are up by ${viewerScore - partnerScore}.`
      : leader === "partner"
        ? `${partnerName} is ahead by ${partnerScore - viewerScore}.`
        : "You are tied.";

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        width: 146,
        flexShrink: 0,
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.18)",
        bgcolor: "rgba(255,255,255,0.1)",
        p: 1.6,
        textAlign: "center",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: "rgba(248,251,255,0.74)", fontSize: "0.72rem", fontWeight: 800 }}>You</Typography>
          <Typography sx={{ color: "#f8fbff", fontSize: "2rem", lineHeight: 1, fontWeight: 900 }}>{viewerScore}</Typography>
        </Box>
        <Typography sx={{ color: "rgba(248,251,255,0.5)", fontSize: "1.4rem", fontWeight: 900 }}>-</Typography>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ color: "rgba(248,251,255,0.74)", fontSize: "0.72rem", fontWeight: 800 }}>{partnerName}</Typography>
          <Typography sx={{ color: "#f8fbff", fontSize: "2rem", lineHeight: 1, fontWeight: 900 }}>{partnerScore}</Typography>
        </Box>
      </Stack>
      <Typography sx={{ mt: 0.8, color: "rgba(248,251,255,0.74)", fontSize: "0.75rem", fontWeight: 800 }}>
        Day {Math.min(dayNumber, duration)} of {duration}
      </Typography>
      <Typography sx={{ mt: 0.4, color: "#f8fbff", fontSize: "0.78rem", lineHeight: 1.25, fontWeight: 700 }}>
        {summary}
      </Typography>
    </Box>
  );
}

function getHeroTone(state) {
  if (state === "active") {
    return {
      background: "linear-gradient(145deg, #11243b 0%, #163457 100%)",
      border: "rgba(133, 220, 188, 0.22)",
      chipBackground: "rgba(99, 208, 156, 0.16)",
      chipColor: "#defef0",
      primaryBackground: "#f8fbff",
      primaryText: "#10213a",
      secondaryBackground: "rgba(255,255,255,0.06)",
      secondaryBorder: "rgba(255,255,255,0.22)",
      secondaryText: "#f8fbff",
    };
  }
  if (state === "drift") {
    return {
      background: "linear-gradient(145deg, #3e2817 0%, #5f3c22 100%)",
      border: "rgba(244, 181, 94, 0.22)",
      chipBackground: "rgba(243, 164, 63, 0.16)",
      chipColor: "#ffe8c6",
      primaryBackground: "#fff4e5",
      primaryText: "#3b2416",
      secondaryBackground: "rgba(255,255,255,0.06)",
      secondaryBorder: "rgba(255,255,255,0.18)",
      secondaryText: "#fff8ef",
    };
  }
  if (state === "reentry") {
    return {
      background: "linear-gradient(145deg, #183327 0%, #255140 100%)",
      border: "rgba(126, 214, 181, 0.22)",
      chipBackground: "rgba(119, 204, 176, 0.16)",
      chipColor: "#e2fff2",
      primaryBackground: "#f1fbf7",
      primaryText: "#173126",
      secondaryBackground: "rgba(255,255,255,0.06)",
      secondaryBorder: "rgba(255,255,255,0.18)",
      secondaryText: "#f8fffb",
    };
  }
  return {
    background: "linear-gradient(145deg, #1b2a40 0%, #293b58 100%)",
    border: "rgba(191, 205, 223, 0.2)",
    chipBackground: "rgba(157, 179, 202, 0.16)",
    chipColor: "#ecf5ff",
    primaryBackground: "#f8fbff",
    primaryText: "#10213a",
    secondaryBackground: "rgba(255,255,255,0.06)",
    secondaryBorder: "rgba(255,255,255,0.2)",
    secondaryText: "#f8fbff",
  };
}

function getActionIcon(label = "") {
  const text = String(label || "").toLowerCase();
  if (
    text.includes("message") ||
    text.includes("chat") ||
    text.includes("encourage") ||
    text.includes("reply") ||
    text.includes("win")
  ) {
    return <ChatBubbleOutlineRoundedIcon />;
  }
  if (text.includes("renew")) {
    return <AutorenewRoundedIcon />;
  }
  return <BoltRoundedIcon />;
}

export default function SprintHeroCard({
  state,
  modeLabel = "",
  stateLabel,
  title,
  body,
  partnerContextLine = "",
  supportingLine = "",
  ring,
  challengeScore = null,
  sprintMode = "support",
  partnerName = "Partner",
  primaryAction,
  secondaryAction,
}) {
  const tone = getHeroTone(state);
  const primaryIcon = getActionIcon(primaryAction?.label);
  const secondaryIcon = secondaryAction ? getActionIcon(secondaryAction?.label) : null;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        p: { xs: 2.25, md: 3 },
        borderRadius: "8px",
        background: tone.background,
        border: `1px solid ${tone.border}`,
        boxShadow: "0 14px 28px rgba(10, 18, 33, 0.14)",
      }}
    >

      <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2.2, md: 3 }} alignItems={{ xs: "flex-start", md: "center" }}>
        {sprintMode === "challenge" && challengeScore ? (
          <CompetitiveScoreCard score={challengeScore} partnerName={partnerName} />
        ) : (
          <SprintMomentumRing {...ring} />
        )}

        <Box sx={{ position: "relative", zIndex: 1, flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
            {modeLabel ? (
              <Chip
                label={modeLabel}
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "rgba(255,255,255,0.08)",
                  color: tone.chipColor,
                  fontWeight: 800,
                  letterSpacing: 0,
                  "& .MuiChip-label": {
                    px: 1.1,
                  },
                }}
              />
            ) : null}
            <Chip
              label={stateLabel}
              size="small"
              sx={{
                alignSelf: "flex-start",
                bgcolor: tone.chipBackground,
                color: tone.chipColor,
                fontWeight: 800,
                letterSpacing: 0,
                "& .MuiChip-label": {
                  px: 1.1,
                },
              }}
            />
          </Stack>

          <Typography
            sx={{
              color: "#f8fbff",
              fontSize: { xs: "1.85rem", md: "2.3rem" },
              lineHeight: 1.05,
              fontWeight: 800,
              letterSpacing: 0,
              maxWidth: 640,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: "rgba(248, 251, 255, 0.78)",
              fontSize: "1rem",
              lineHeight: 1.6,
              fontWeight: 600,
              maxWidth: 560,
            }}
          >
            {body}
          </Typography>
          {partnerContextLine ? (
            <Typography
              sx={{
                mt: 1.15,
                color: "#ffffff",
                fontSize: "0.98rem",
                lineHeight: 1.45,
                fontWeight: 700,
                maxWidth: 560,
              }}
            >
              {partnerContextLine}
            </Typography>
          ) : null}
          {supportingLine ? (
            <Typography
              sx={{
                mt: 0.55,
                color: "rgba(248, 251, 255, 0.74)",
                fontSize: "0.94rem",
                lineHeight: 1.45,
                maxWidth: 560,
              }}
            >
              {supportingLine}
            </Typography>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              startIcon={primaryIcon}
              onClick={primaryAction.onClick}
              sx={{
                minHeight: 46,
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 800,
                px: 2.1,
                bgcolor: tone.primaryBackground,
                color: tone.primaryText,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: tone.primaryBackground,
                  boxShadow: "none",
                  opacity: 0.92,
                },
              }}
            >
              {primaryAction.label}
            </Button>
            {secondaryAction ? (
              <Button
                variant="outlined"
                startIcon={secondaryIcon}
                onClick={secondaryAction.onClick}
                sx={{
                  minHeight: 46,
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.1,
                  borderColor: tone.secondaryBorder,
                  bgcolor: tone.secondaryBackground,
                  color: tone.secondaryText,
                  "&:hover": {
                    borderColor: tone.secondaryBorder,
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
